import { ReactNode, useMemo } from 'react';

import dayjs from 'dayjs';

import { tokens } from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useWorkloadReliabilitySummariesQuery } from '@cast/shared-cost-data-access';
import { WorkloadReliabilitySummariesResponseItem } from '@cast/types';
import { useReportCapabilitiesQuery } from '@cast/split-me/hooks';
import {
  useAllPagesWorkloadReliabilityMetricsQuery,
  deriveSloStatus,
} from '@cast/split-me/features/network-intelligence/service-map-v2';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

export type WorkloadScatterPoint = {
  id: string;
  name: string;
  requestRate: number;
  errorRate: number;
  errorRatePercent: number;
  latencyP95Ms: number;
  health: 'healthy' | 'warning' | 'breach' | 'unknown';
};

export type LatencyBucket = {
  label: string;
  count: number;
  colorCategory: 'green' | 'yellow' | 'red';
};

export type TopOffenderMetric =
  | 'errorRate'
  | 'errorRatePercent'
  | 'latencyP95Ms';

export type TopOffender = {
  id: string;
  name: string;
  value: ReactNode;
  color: string;
};

const formatTopOffenderValue = (
  metric: TopOffenderMetric,
  value: number
): string => {
  if (metric === 'latencyP95Ms') return `${(value * 1000).toFixed(0)}ms`;
  if (metric === 'errorRatePercent') return `${value.toFixed(2)}%`;
  return `${value.toFixed(3)}/s`;
};

type MapSummaryItemToTopOffenderArgs = {
  item: WorkloadReliabilitySummariesResponseItem;
  metric: TopOffenderMetric;
  workloadId?: string;
};

const mapSummaryItemToTopOffender = ({
  item,
  metric,
  workloadId,
}: MapSummaryItemToTopOffenderArgs): TopOffender => ({
  id:
    workloadId ?? `${item.workloadName}-${item.workloadType}-${item.namespace}`,
  name: item.workloadName ?? '',
  value: formatTopOffenderValue(metric, item.metric ?? 0),
  color: tokens.theme.data.red500,
});

const LATENCY_BUCKETS = [
  { label: '<50ms', minS: 0, maxS: 0.05, category: 'green' as const },
  { label: '50-100ms', minS: 0.05, maxS: 0.1, category: 'green' as const },
  { label: '100-150ms', minS: 0.1, maxS: 0.15, category: 'yellow' as const },
  { label: '150-200ms', minS: 0.15, maxS: 0.2, category: 'yellow' as const },
  { label: '200-250ms', minS: 0.2, maxS: 0.25, category: 'yellow' as const },
  { label: '250-300ms', minS: 0.25, maxS: 0.3, category: 'yellow' as const },
  { label: '300-350ms', minS: 0.3, maxS: 0.35, category: 'yellow' as const },
  { label: '350-400ms', minS: 0.35, maxS: 0.4, category: 'yellow' as const },
  { label: '400-450ms', minS: 0.4, maxS: 0.45, category: 'yellow' as const },
  { label: '450-500ms', minS: 0.45, maxS: 0.5, category: 'yellow' as const },
  { label: '>500ms', minS: 0.5, maxS: Infinity, category: 'red' as const },
];

export const useReliabilityDashboardData = () => {
  const { cluster } = useCluster();
  const { getRange, timeZone } = useInventoryWorkloadsTimeRangeSelection();

  // Memoize time range: getRange() uses dayjs() internally for rolling presets
  // (e.g. Last7Days → getRollingRange), producing millisecond-precision timestamps
  // on every call. Without memoization this changes the TanStack Query key on every
  // render, triggering an infinite fetch loop.
  const { startTime, endTime } = useMemo(() => {
    const range = getRange();
    return {
      startTime: dayjs.utc(range.from).toISOString(),
      endTime: dayjs.utc(range.to).toISOString(),
    };
  }, [getRange]);

  // Stable hourly window for capabilities check (rounded to hour boundaries)
  const { capStart, capEnd } = useMemo(() => {
    const hourStart = dayjs().startOf('hour');
    return {
      capStart: hourStart.subtract(1, 'hour').toISOString(),
      capEnd: hourStart.endOf('hour').toISOString(),
    };
  }, []);

  const {
    data: capabilitiesData,
    isLoading: isCapabilitiesLoading,
    refetch: refetchCapabilities,
  } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime: capStart,
    endTime: capEnd,
    enabled: !!cluster.id,
  });

  const hasReliabilityMetrics =
    capabilitiesData?.reliabilityMetricsDataAvailable ?? false;
  const showNotInstalled = !isCapabilitiesLoading && !hasReliabilityMetrics;

  const {
    data: summaries,
    isLoading: isSummariesLoading,
    error: summariesError,
    refetch: refetchSummaries,
  } = useWorkloadReliabilitySummariesQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    timezone: timeZone,
    enabled: !!cluster.id && hasReliabilityMetrics,
  });

  const {
    items: workloadItems,
    isFetching,
    error,
    refetch: refetchMetrics,
  } = useAllPagesWorkloadReliabilityMetricsQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    timezone: timeZone,
    enabled: !!cluster.id && hasReliabilityMetrics,
  });

  const scatterData = useMemo<WorkloadScatterPoint[]>(() => {
    if (!workloadItems?.length) return [];
    return workloadItems
      .filter((item) => item.reliability?.requestRate !== undefined)
      .map((item) => {
        const r = item.reliability!;
        return {
          id: item.id,
          name: item.workloadName ?? item.id,
          requestRate: r.requestRate ?? 0,
          errorRate: r.errorRate ?? 0,
          errorRatePercent: r.errorRatePercent ?? 0,
          latencyP95Ms: (r.latencyP95 ?? 0) * 1000,
          health: deriveSloStatus(r.latencyP95, r.errorRatePercent),
        };
      });
  }, [workloadItems]);

  const { healthCounts, healthPercentages, trafficByHealth, latencyMetrics } =
    useMemo(() => {
      const counts = { healthy: 0, warning: 0, breach: 0 };
      const reqByHealth = { healthy: 0, warning: 0, breach: 0 };
      let totalKnown = 0;
      let totalReq = 0;
      let under100ms = 0;
      let between100And500ms = 0;
      let over500ms = 0;

      for (const pt of scatterData) {
        if (pt.health === 'unknown') continue;
        totalKnown++;
        counts[pt.health]++;
        reqByHealth[pt.health] += pt.requestRate;
        totalReq += pt.requestRate;
        if (pt.latencyP95Ms < 100) under100ms++;
        else if (pt.latencyP95Ms < 500) between100And500ms++;
        if (pt.latencyP95Ms >= 500) over500ms++;
      }

      const pct = (n: number) =>
        totalKnown > 0 ? Math.round((n / totalKnown) * 100) : 0;
      const trafficPct = (n: number) =>
        totalReq > 0 ? Math.round((n / totalReq) * 100) : 0;

      return {
        healthCounts: counts,
        healthPercentages: {
          healthy: pct(counts.healthy),
          warning: pct(counts.warning),
          breach: pct(counts.breach),
        },
        trafficByHealth: {
          healthy: {
            percent: trafficPct(reqByHealth.healthy),
            reqPerSec: reqByHealth.healthy,
          },
          warning: {
            percent: trafficPct(reqByHealth.warning),
            reqPerSec: reqByHealth.warning,
          },
          breach: {
            percent: trafficPct(reqByHealth.breach),
            reqPerSec: reqByHealth.breach,
          },
        },
        latencyMetrics: {
          under100msCount: under100ms,
          under100msPercent: pct(under100ms),
          between100And500msCount: between100And500ms,
          between100And500msPercent: pct(between100And500ms),
          over500msCount: over500ms,
          over500msPercent: pct(over500ms),
        },
      };
    }, [scatterData]);

  const latencyDistribution = useMemo<LatencyBucket[]>(() => {
    const counts = LATENCY_BUCKETS.map(() => 0);
    for (const pt of scatterData) {
      const latencyS = pt.latencyP95Ms / 1000;
      const idx = LATENCY_BUCKETS.findIndex(
        (b) => latencyS >= b.minS && latencyS < b.maxS
      );
      if (idx >= 0) counts[idx]++;
    }
    return LATENCY_BUCKETS.map((b, i) => ({
      label: b.label,
      count: counts[i],
      colorCategory: b.category,
    }));
  }, [scatterData]);

  const workloadIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const point of scatterData) {
      map.set(point.name, point.id);
    }
    return map;
  }, [scatterData]);

  const getTopOffenders = useMemo(
    () =>
      (metric: TopOffenderMetric): TopOffender[] => {
        const listKey =
          metric === 'errorRate'
            ? 'highestErrorRate'
            : metric === 'errorRatePercent'
            ? 'highestErrorRatePercent'
            : 'highestLatencyP95';
        const items: WorkloadReliabilitySummariesResponseItem[] =
          summaries?.[listKey] ?? [];

        return items.slice(0, 10).map((item) =>
          mapSummaryItemToTopOffender({
            item,
            metric,
            workloadId: workloadIdByName.get(item.workloadName ?? ''),
          })
        );
      },
    [summaries, workloadIdByName]
  );

  return {
    scatterData,
    healthCounts,
    healthPercentages,
    trafficByHealth,
    latencyDistribution,
    latencyMetrics,
    getTopOffenders,
    isLoading: isFetching || isCapabilitiesLoading || isSummariesLoading,
    isFetching,
    error: error || summariesError,
    showNotInstalled,
    refetch: () => {
      refetchMetrics?.();
      refetchSummaries?.();
      refetchCapabilities?.();
    },
  };
};
